import { css, html, LitElement } from 'lit'
import { property } from 'lit/decorators.js'
import { unsafeHTML } from 'lit/directives/unsafe-html.js'
import { safeCustomElement } from '@/utilities/custom-element'
import type { TablePlan, TableSection } from './types'

// Default plans data
const defaultPlans: TablePlan[] = [
  {
    id: 'starter',
    title: 'Starter',
    subtitle: 'Free',
    buttonLabel: 'Select Plan',
    buttonClick: () => {},
    pro: false,
    variant: 'pro-starter',
  },
  {
    id: 'pro',
    title: 'Pro',
    subtitle: '$16.7 / month',
    buttonLabel: 'Select Plan',
    buttonClick: () => {},
    pro: true,
    variant: 'pro-pro',
  },
  {
    id: 'proTeam',
    title: 'Pro Team',
    subtitle: '$16.7 / month',
    buttonLabel: 'Select Plan',
    buttonClick: () => {},
    pro: true,
    variant: 'pro-team',
  },
]

// Default sections data
const defaultSections: TableSection[] = [
  {
    header: {
      title: 'Be inspired.',
    },
    rows: [
      {
        title: 'News, Trends and interviews',
        starter: '<span class="check-pink"></span>',
        pro: '<span class="check-orange"></span>',
        proTeam: '<span class="check-orangered"></span>',
      },
      {
        title: 'Weekly email newsletter',
        starter: '<span class="check-pink"></span>',
        pro: '<span class="check-orange"></span>',
        proTeam: '<span class="check-orangered"></span>',
      },
      {
        title: 'Models rankings',
        starter: '<span class="check-pink"></span>',
        pro: '<span class="check-orange"></span>',
        proTeam: '<span class="check-orangered"></span>',
      },
      {
        title: 'Notifications from your favorites',
        starter: '<span class="check-pink"></span>',
        pro: '<span class="check-orange"></span>',
        proTeam: '<span class="check-orangered"></span>',
      },
    ],
  },
  {
    header: {
      title: 'Discover & Connect.',
    },
    rows: [
      {
        title: 'Explore Models & Creatives',
        starter: 'Up to 50 pages / mo.',
        pro: 'Unlimited',
        proTeam: 'Unlimited',
      },
      {
        title: 'Weekly email newsletter',
        starter: '<span class="check-pink"></span>',
        pro: '<span class="check-orange"></span>',
        proTeam: '<span class="check-orangered"></span>',
      },
      {
        title: 'Models rankings',
        starter: '<span class="check-pink"></span>',
        pro: '<span class="check-orange"></span>',
        proTeam: '<span class="check-orangered"></span>',
      },
      {
        title: 'Notifications from your favorites',
        starter: '<span class="check-pink"></span>',
        pro: '<span class="check-orange"></span>',
        proTeam: '<span class="check-orangered"></span>',
      },
    ],
  },
  {
    header: {
      title: 'Be Seen. Manage your Brand.',
    },
    rows: [
      {
        title: 'News, Trends and interviews',
        starter: '',
        pro: '<span class="check-orange"></span>',
        proTeam: '<span class="check-orangered"></span>',
      },
      {
        title: 'Weekly email newsletter',
        starter: '',
        pro: '<span class="check-orange"></span>',
        proTeam: '<span class="check-orangered"></span>',
      },
      {
        title: 'Models rankings',
        starter: '',
        pro: '',
        proTeam: '<span class="check-orangered"></span>',
      },
      {
        title: 'Notifications from your favorites',
        starter: '',
        pro: '',
        proTeam: '<span class="check-orangered"></span>',
      },
    ],
  },
]

@safeCustomElement('mdc-pricing-table')
export class MdcPricingTable extends LitElement {
  @property({ type: Array }) plans: TablePlan[] = defaultPlans
  @property({ type: Array }) sections: TableSection[] = defaultSections

  static styles = css`
		:host {
			display: block;
			font-family: var(--typography-font-family-sans);
		}

		.mdc-table-wrapper {
			padding-left: var(--spacing-150, 1.5rem) !important;
			padding-right: var(--spacing-150, 1.5rem) !important;
		}

		.mdc-table {
			height: 1px;
			width: 100%;
			border-collapse: collapse;
		}

		thead.mdc-table__main-header {
			background-color: transparent;
		}

		thead {
			background-color: var(--color-functional-gray-25, #fafafa);
		}

		thead th {
			font-size: 0.875rem;
			font-weight: 600;
			line-height: 1.2;
			letter-spacing: -0.01em;
			text-align: left;
			padding: 0.75rem 1rem;
		}

		tbody tr:not(:last-child) {
			border-bottom: 1px solid var(--color-functional-gray-200, #e5e5e5);
		}

		tbody:last-of-type tr:last-of-type {
			border-bottom: 1px solid var(--color-functional-gray-200, #e5e5e5);
		}

		tbody td:first-child {
			text-align: left;
		}

		tbody td {
			font-size: 0.875rem;
			color: var(--color-functional-gray-black, #111111);
			font-weight: 450;
			line-height: 1.3;
			text-align: center;
			padding: 1rem;
		}

		tbody td span[class*='check-'] {
			display: block;
			height: 1.3125rem;
			width: 1.3125rem;
			background-size: contain;
			background-repeat: no-repeat;
			margin: auto;
		}

		.mdc-table__plan-header {
			display: grid;
			align-items: center;
			justify-content: center;
			align-content: space-between;
			text-align: center;
			height: 100%;
		}

		.mdc-title {
			margin: 0.5rem 0;
			font-size: 1.25rem;
			line-height: 1.5625rem;
			font-weight: 600;
		}

		.mdc-title--pro {
			display: inline-flex;
			column-gap: 0.25rem;
			align-items: center;
			justify-content: center;
		}

		.mdc-title--pro::after {
			content: 'PRO';
			text-transform: uppercase;
			font-size: 0.5625rem;
			line-height: 0.6875rem;
			border: 1px solid var(--color-functional-amber, #ffb800);
			border-radius: 0.25rem;
			color: var(--color-functional-amber, #ffb800);
			padding: 0.3125rem 0.5rem;
			letter-spacing: calc(0.5625rem * 0.05);
			font-weight: 450;
		}

		.mdc-subtitle {
			margin: 0.5rem 0;
			font-size: 1rem;
			line-height: 1.3125rem;
			color: var(--color-functional-gray-black, #111111);
			font-weight: 450;
		}

		.mdc-button {
			margin: 0.5rem 0;
			text-transform: uppercase;
			font-size: 0.6875rem;
			line-height: 0.8125rem;
			min-width: 8.125rem;
			white-space: nowrap;
			align-self: end;
			font-weight: normal;
		}

		/* breakpoints.lg - 1 */
		@media screen and (max-width: 1023px) {
			.mdc-table-wrapper {
				padding-left: 0 !important;
				padding-right: 0 !important;
			}

			.mdc-table {
				table-layout: fixed;
			}

			thead th {
				padding: 0.5rem;
				font-size: 0.75rem;
				line-height: 1rem;
			}

			tbody td {
				padding: 0.5rem;
				font-size: 0.75rem;
				line-height: 1rem;
			}

			tbody td:first-child {
				max-width: 5.375rem;
			}

			.mdc-table__main-header th {
				height: inherit;
				padding: 0;
			}

			.mdc-table__plan-header {
				justify-content: center;
				height: 100%;
			}

			.mdc-table__plan-header .mdc-title {
				margin: 0.25rem 0;
				font-size: 0.75rem;
				line-height: 1rem;
			}

			.mdc-table__plan-header .mdc-title--pro::after {
				font-size: 0.3125rem;
				line-height: 0.375rem;
				padding: 0.1875rem 0.25rem;
				letter-spacing: calc(0.3125rem * 0.05);
			}

			.mdc-table__plan-header .mdc-subtitle {
				margin: 0.25rem 0;
				font-size: 0.625rem;
				line-height: 0.75rem;
			}

			.mdc-table__plan-header .mdc-button {
				padding: 0.375rem 0.5rem;
				min-width: 5rem;
			}
		}
	`

  render() {
    return html`
			<div class="mdc-table-wrapper">
				<table class="mdc-table">
					<thead class="mdc-table__main-header">
						<tr>
							<th></th>
							${this.plans.map(
                (plan) => html`
									<th>
										<div class="mdc-table__plan-header">
											<h3 class="mdc-title ${plan.pro ? 'mdc-title--pro' : ''}">${plan.title}</h3>
											<p class="mdc-subtitle">${plan.subtitle}</p>
											<mdc-button variant=${plan.variant} size="md">${plan.buttonLabel}</mdc-button>
										</div>
									</th>
								`
              )}
						</tr>
					</thead>

					${this.sections.map(
            (section) => html`
							<thead>
								<tr>
									<th colspan=${this.plans.length + 1}>${section.header.title}</th>
								</tr>
							</thead>
							<tbody>
								${section.rows.map(
                  (row) => html`
										<tr>
											<td>${row.title}</td>
											${this.plans.map(
                        (plan) =>
                          html`<td>${unsafeHTML(row[plan.id as keyof typeof row] || '')}</td>`
                      )}
										</tr>
									`
                )}
							</tbody>
						`
          )}
				</table>
			</div>
		`
  }
}
